import { ArrowLeft, Menu } from "lucide-react";
import { useTranslation } from "react-i18next";

import { NavLink, useLocation } from "react-router-dom";

import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { NotificationsBell } from "@/components/shared/NotificationsBell";
import { StudioContextBar } from "@/components/shared/StudioContextBar";
import { TenantContextBar } from "@/components/shared/TenantContextBar";
import { Button } from "@/components/ui/button";
import { usePageBackAction } from "@/contexts/PageBackContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { LG_QUERY, useMediaQuery } from "@/hooks/useMediaQuery";
import { DashboardViewControl } from "@/pages/dashboard/DashboardViewControl";
import { useTenantSwitcher } from "@/contexts/TenantSwitcherContext";

export function Header(): JSX.Element {
  const { toggleSidebar } = useSidebar();
  const { t } = useTranslation();
  const { activeTenant } = useTenantSwitcher();
  const isDesktop = useMediaQuery(LG_QUERY);
  const pageBack = usePageBackAction();
  const onAccountantHome = useLocation().pathname.startsWith("/accountant");

  return (
    <header className="fx-topbar relative flex h-16 items-center justify-between gap-2 px-4 sm:px-7">
      <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
        {/* On a sub-page the back arrow REPLACES the hamburger rather than
            joining it. Both would mean two navigation controls competing in
            the same corner, and back is the one you want on a detail screen —
            the drawer is still reachable by going back to the list. Desktop
            keeps the sidebar visible, so neither control applies there. */}
        {pageBack ? (
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 lg:hidden"
            onClick={pageBack}
            aria-label={t("pages.common.back")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 lg:hidden"
            onClick={toggleSidebar}
            aria-label={t("header.toggleNavigation")}
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}
        {/* In normal flow, NOT absolutely centred. The centred variant is only
            stable when both flanks are narrow and roughly symmetric; once the
            view toggle moved into the header the right cluster grew to ~234px
            and simply overlapped the logo on a phone. Sitting next to the
            hamburger, the logo cannot collide with anything no matter how the
            right side grows. */}
        {/* The real brand asset, not a hand-rolled "F" tile plus a text
            wordmark — that approximation drifted from the actual logo and was
            the only place in the app not using /brand. The dark variant swaps
            via CSS because these are flat SVGs with baked-in colour. */}
        <NavLink
          to="/"
          className="flex shrink-0 items-center lg:hidden"
          aria-label="Fiscalo"
        >
          <img
            src="/brand/fiscalo-logo.svg"
            alt="Fiscalo"
            className="h-7 shrink-0 dark:hidden"
          />
          <img
            src="/brand/fiscalo-logo-white.svg"
            alt="Fiscalo"
            className="hidden h-7 shrink-0 dark:block"
          />
        </NavLink>
        <div className="hidden min-w-0 lg:flex lg:items-center gap-3">
          <TenantContextBar />
          <StudioContextBar />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 sm:gap-3">
        {/* Desktop only. Below lg the toggle renders in the dashboard body
            instead — see DashboardHeader. Gated on a media query rather than
            `hidden lg:flex` so only one copy is ever mounted: the hint popover
            anchors to this control, and an anchor on a hidden element gets
            positioned in the corner.
            Hidden on /accountant for the same reason Sidebar hides its
            tenant-scoped nav there: this preference drives the tenant
            dashboard and that nav, and an accountant looking at their cartera
            is not inside any one client, so the toggle governed nothing they
            could see. Navigating INTO a client leaves /accountant, which
            brings it back. */}
        {activeTenant && isDesktop && !onAccountantHome && (
          <DashboardViewControl placement="header" />
        )}
        {activeTenant && <NotificationsBell />}
        <LanguageSwitcher />
      </div>
    </header>
  );
}
